import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import {
    bucketsToCountByDay,
    bucketsToCountByMonth,
    bucketsToCountByWeek,
    buildGameAggregatesFromStats,
    computeDayPopularityFromDayBuckets,
    reconcileDayCountsWithTableTotal,
    sumBucketValues,
} from "./dashboardAggregations";
import { selectedRangeChartTitle } from "./dashboardPeriods";

dayjs.extend(isoWeek);

/** Avoid Chart.js drawing a 1px border that looks like a tiny non-zero bar. */
function zeroSafeBorderWidth(fallback = 1) {
    return (ctx) => {
        const value = Number(ctx.dataset.data[ctx.dataIndex]);
        return value > 0 ? fallback : 0;
    };
}

function buildSelectedRangeSeries(subdivision, buckets, startDate, endDate, tableTotal) {
    if (subdivision === "week") {
        const byWeek = bucketsToCountByWeek(buckets, startDate, endDate);
        const keys = Object.keys(byWeek);
        return {
            labels: keys.map((key) => {
                const start = dayjs(key);
                const end = start.endOf("isoWeek");
                return `${start.format("D MMM")}–${end.format("D MMM")}`;
            }),
            counts: keys.map((key) => byWeek[key]),
        };
    }
    if (subdivision === "month") {
        const byMonth = bucketsToCountByMonth(buckets, startDate, endDate);
        const keys = Object.keys(byMonth);
        return {
            labels: keys.map((key) => dayjs(`${key}-01`).format("MMM YYYY")),
            counts: keys.map((key) => byMonth[key]),
        };
    }

    const byDay = reconcileDayCountsWithTableTotal(
        bucketsToCountByDay(buckets, startDate, endDate),
        tableTotal,
    );
    const keys = Object.keys(byDay);
    return {
        labels: keys.map((key) => dayjs(key).format("D MMM")),
        counts: keys.map((key) => byDay[key]),
    };
}

/**
 * Build Chart.js datasets and labels from GET /bookings/stats bucket data.
 */
export function buildDashboardChartViewModel({
    colors,
    baseFilters,
    periods,
    statsData,
    experienceNameById,
    weekdayLabels,
}) {
    const { previousMonthLabel, monthBeforeLabel, sameMonthLastYearLabel } = periods;

    const {
        selectedRange,
        selectedRangeSubdivision = "day",
        periodBookingsByDay,
        filterStats,
    } = statsData;

    // Completed calendar months only (no current MTD), same idea as the 12-month report.
    const monthlyKPI = {
        previousMonth: sumBucketValues(periodBookingsByDay.prevMonth),
        monthBefore: sumBucketValues(periodBookingsByDay.monthBefore),
        sameMonthLastYear: sumBucketValues(periodBookingsByDay.sameMonthLastYear),
        previousMonthLabel,
        monthBeforeLabel,
        sameMonthLastYearLabel,
    };

    const selectedRangeSeries = buildSelectedRangeSeries(
        selectedRangeSubdivision,
        selectedRange.bookings ?? selectedRange.bookingsByDay,
        dayjs(baseFilters.startDate),
        dayjs(baseFilters.endDate),
        filterStats?.count,
    );

    const selectedRangeDatasets = [
        {
            label: "Selected period",
            data: selectedRangeSeries.counts,
            backgroundColor: colors.seriesPrimaryFill,
            borderColor: colors.seriesPrimaryStroke,
            borderWidth: zeroSafeBorderWidth(1),
            borderRadius: 4,
        },
    ];

    // Same weekdays one year earlier, aligned by position (day subdivision only).
    if (selectedRangeSubdivision === "day" && selectedRange.lastYearByDay) {
        const lastYearByDay = bucketsToCountByDay(
            selectedRange.lastYearByDay,
            dayjs(baseFilters.startDate).subtract(364, "day"),
            dayjs(baseFilters.endDate).subtract(364, "day"),
        );
        selectedRangeDatasets.push({
            label: "Same weekdays last year",
            data: Object.values(lastYearByDay),
            backgroundColor: colors.seriesTertiarySoft,
            borderWidth: 0,
            borderRadius: 4,
        });
    }

    const monthComparisonLabels = [previousMonthLabel, monthBeforeLabel, sameMonthLastYearLabel];
    const monthComparisonDatasets = [
        {
            label: "Bookings",
            data: [monthlyKPI.previousMonth, monthlyKPI.monthBefore, monthlyKPI.sameMonthLastYear],
            backgroundColor: [
                colors.seriesPrimaryFill,
                colors.seriesSecondaryFill,
                colors.seriesTertiaryFill,
            ],
            borderWidth: 0,
        },
    ];

    const { rankedByCount } = buildGameAggregatesFromStats(
        selectedRange.playByExperience,
        experienceNameById,
    );
    const dayPop = computeDayPopularityFromDayBuckets(
        selectedRange.bookingsByDay,
        selectedRange.playersByDay,
        weekdayLabels,
    );

    const popularDayLabels = dayPop.weekdayLabels;
    const popularDayDatasets = [
        {
            label: "Bookings",
            data: popularDayLabels.map((label) => dayPop.bookingCounts[label] || 0),
            backgroundColor: colors.seriesSecondaryFill,
            borderWidth: 0,
        },
        {
            label: "Players",
            data: popularDayLabels.map((label) => dayPop.playerTotals[label] || 0),
            backgroundColor: colors.seriesTertiaryFill,
            borderWidth: 0,
        },
    ];

    return {
        selectedRangeChartTitle: selectedRangeChartTitle(selectedRangeSubdivision),
        selectedRangeLabels: selectedRangeSeries.labels,
        selectedRangeDatasets,
        monthComparisonLabels,
        monthComparisonDatasets,
        topGames: rankedByCount.slice(0, 10),
        popularDayLabels,
        popularDayDatasets,
    };
}
